import { Component, ElementRef, Injector, Input, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { WidgetBaseComponent } from '../../widgetBase';
import { first } from 'rxjs/operators';
import { environment } from '../../../../../../environments/environment';
import { SafeHtml } from '@angular/platform-browser';
import { of } from 'rxjs';
import { HashtagDirective } from '../../../hashtags/hashtag.directive';
import { AppLocalizationService } from '../../../../../services/AppLocalizationService';
import { EditorComponent, EditorPasteEvent } from '@progress/kendo-angular-editor';
import { IEditorObject } from './editor-object';
import { textWidgetLayouts } from './text-widget-layouts';

/**
 * Text Widget
 */
@Component({
  selector: 'text-widget',
  templateUrl: './text-widget.component.html',
  styleUrls: ['./text-widget.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class TextWidgetComponent extends WidgetBaseComponent implements OnInit, OnDestroy {

  /**
   * Widget Guid
   */
  @Input() widgetGuid: string;

  /**
   * IFRAME editor object
   */
  editorIframe: IEditorObject = { editor: null };

  /**
   * Inline editor object
   */
  editorInline: IEditorObject = { editor: null };

  /**
   * IFRAME editor component
   */
  @ViewChild('iframeEditor', { static: false })

  /**
   * IFRAME editor
   */
  set iframeEditor(editor: EditorComponent) {
    if (editor) {
      this.editorIframe.editor = editor;
      this.widgetResized();//initial setup
    }
  }

  /**
   * Inline editor component
   */
  @ViewChild('inlineEditor', { static: false })

  /**
   * Inline editor
   */
  set inlineEditor(editor: EditorComponent) {
    if (editor) {
      this.editorInline.editor = editor;
      this.widgetResized();//initial setup
    }
  }

  /**
   * Widget's preview content
   */
  public previewContent: SafeHtml = '';

  /**
   * Widget's text
   */
  private _textWidgetText;

  /**
   * layoutHasChanged flag
   */
  private layoutHasChanged = false;

  /**
   * Widget's text getter
   */
  public get textWidgetText() {
    return this._textWidgetText;
  }

  /**
   * Widget's text setter
   */
  public set textWidgetText(value: string) {
    this._textWidgetText = value;
  }

  /**
   * Is widget resizable
   */
  public resizable = false;

  /**
   * Should widget use IFRAME? (Kendo editor is used)
   */
  get useIframe(): boolean { return this.widgetConfig.widgetSettings.showKendoEditor; }

  /**
   * Paste event name constant
   */
  pasteEventName = 'paste';

  /**
   * Pasted data
   */
  pastedData = null;

  /**
   * Should we remove unwanted tags
   */
  static readonly shouldRemoveUnwantedTags = false;

  /**
   * Reference to hashtag directive
   */
  @ViewChild(HashtagDirective) hashtagDirective;

  /**
* Constructor
* @param widgetService WidgetService
* @param ais AngularIntegrationService
* @param componentEl ElementRef
*/
  constructor(injector: Injector,
    private componentEl: ElementRef,
    private appLocalization: AppLocalizationService) {
    super(injector, 'text', false);
  }

  /**
   * Initializes the component
   */
  ngOnInit() {
    // will call registerEvents and onInitialized when config (this.widgetConfig and this.page) is ready
    //little different approach than in other widgets because we have to deal with typeId


    /*
    FOR DISCUSSION: maybe load from API (DB: ref_report_textwidget), not defined yet 100% => see layouts down the source
    */
    const data = this.layouts;
    this.reflists['ref_report_textwidget'] = data.filter(o => o.id !== 0);

    this.availableLayouts = this.reflists['ref_report_textwidget'].map(o => o.description);

    this.initWidgetConfig();

    this.componentEl.nativeElement.addEventListener(this.pasteEventName, this.pasteEventHandler.bind(this));
  }

  /**
   * Destroy/cleanup the component
   */
  ngOnDestroy() {
    super.ngOnDestroy();
    this.componentEl.nativeElement.removeEventListener(this.pasteEventName, this.pasteEventHandler);
  }

  /**
   * Set the new value
   * @param newValue
   */
  public setNewValue(newValue: string) {
    if (!this.editMode) {
      this.previewContent = TextWidgetComponent.removeUnwantedTag(newValue);
    } else if (!this.layoutHasChanged) {
      this.textWidgetText = newValue;
      this.previewContent = TextWidgetComponent.removeUnwantedTag(newValue);

      this.widgetData.textWidgetText = newValue;
      this.forceDirty();
    } else {
      this.layoutHasChanged = false;
      //Workaround for Bug 19440: Text widget populated and suddenly replaced by "Lorem ipsum text"
      //Without this the KendoEditor does not update to the new layout the first time editmode is activated
      const textWidgetText = this.textWidgetText;
      this.textWidgetText = "";
      setTimeout(() => {
        this.textWidgetText = textWidgetText;
        this.forceDirty();
      }, 0);
    }

    // setNewValue might be called by HashtagDirective in the middle of Angular change detection
    // so inform Angular to run additional detection cycle after we modify displayed data
    this.ref.detectChanges();
  }

  /**
   * Called by base class to let widget register to various app events (like saving, loading, resizing etc)
   */
  registerEvents(): void {
    this.useDefaultServiceSaveChangesHandler('text', this.provideSaveObject.bind(this));
    this.useTemplateServiceSaveChangesHandler('text', this.provideSaveObjectTemplate.bind(this));

    this.onEvents.push(this.eventBus.subscribeEvent('widgetToggleKendoEditor', (id) => {
      const isMyEvent = id === this.widgetConfig.widgetSettings.id;
      if (isMyEvent) {
        this.widgetConfig.widgetSettings.showKendoEditor = !this.widgetConfig.widgetSettings.showKendoEditor;
      }
    }));
  }

  /**
   * Called by base class to inform widget that it is initialized
   * Mostly used to define where widget data should be loaded from
   */
  onInitialized(): void {
    if (!this.widgetConfig.hashtagSupported) {
      this.widgetConfig.hashtagSupported = true;
    }

    this.widgetConfig.widgetSettings.hasKendoEditor = !!this.widgetConfig.widgetSettings.hasKendoEditor;//force boolean

    this.widgetConfig.widgetSettings.availableLayouts = this.availableLayouts;
    this.currentLayout = this.widgetConfig.widgetSettings.currentLayout;

    if (this.currentLayout === "ChosenOnAdd") {
      this.chooseNextLayout();
    }

    const typeId: number = this.reflists['ref_report_textwidget'].find(o => o.description === this.currentLayout).id;
    const languageDefinitionId: number = this.appLocalization.getReportDesignerDictionaryConfig().id;

    this.mockupData$ = this.widgetService.loadMockup('text');
    /* we always need mockup data, even in report or print mode */
    this.forceLoadingMockupData = true;

    this.templateData$ = this.templateId ? this.loadTemplateForType(typeId, languageDefinitionId) : of(null);

    if (this.templateId) {
      this.widgetData$ = this.templateData$;
      this.mockup = false;
    }
    else {
      this.widgetData$ = this.mockup ?
        this.mockupData$
        : this.loadForType(typeId, languageDefinitionId);
    }

    this.widgetConfig.widgetSettings.showKendoEditor = !!this.widgetConfig.widgetSettings.showKendoEditor;//ensure bool
  }

  /**
   * Load widget data for given typeId
   * @param typeId {number} Text widget type Id
   * @param languageDefinitionId {number} Language definition Id
   */
  loadForType(typeId: number, languageDefinitionId: number) {
    const reportId = this.rdStorage.getReport() || 0;
    return this.widgetService.loadFromWidgetResource(`text?id=${reportId}&typeId=${typeId}&languageDefinitionId=${languageDefinitionId}`);
  }

  /**
   * Load widget template for given typeId
   * @param typeId {number} Text widget type Id
   * @param languageDefinitionId {number} Language definition Id
   */
  loadTemplateForType(typeId: number, languageDefinitionId: number) {
    const templateId = this.templateId || 0;
    return this.widgetService.loadFromWidgetResource(`text/template?id=${templateId}&typeId=${typeId}&languageDefinitionId=${languageDefinitionId}`);
  }

  /**
   * Called by base class to let widget preprocess loaded data if required
   */
  preprocessLoadedData(data) {
    if (this.mockup || data.textWidgetText == null) {
      data.textWidgetText = `${this.currentLayout}<br/><br/>${this.mockupData.textWidgetText}`;
    }

    this.previewContent = TextWidgetComponent.removeUnwantedTag(data.textWidgetText);
    this.textWidgetText = data.textWidgetText;

    data.languageDefinitionId = this.appLocalization.getReportDesignerDictionaryConfig().id;

    return data;
  }

  /**
   * Called by base class in save process. Widget should return object to be saved
   */
  async provideSaveObject(): Promise<any> {
    return this.widgetData;
  }

  /**
   * Called by base class in template save process. Widget should return object to be saved
   */
  async provideSaveObjectTemplate(oid: number): Promise<any> {
    this.widgetData.templateId = oid;
    return this.widgetData;
  }

  /**
   * Choose next available layout when adding text widget into report document
   */
  private chooseNextLayout() {
    const types = this.reflists['ref_report_textwidget'];

    const activeDocument = this.rdStorage.getActiveDocument();
    const usedLayouts: string[] = [];
    for (const page of activeDocument.data.pages) {
      for (const widget of page.widgets) {
        if (widget.widgetId === this.widgetConfig.widgetId) {//just text widgets
          if (usedLayouts.indexOf(widget.widgetSettings.currentLayout) < 0) {
            usedLayouts.push(widget.widgetSettings.currentLayout);
          }
        }
      }
    }

    for (const type of types) {
      const typeDescription = type.description;
      if (usedLayouts.indexOf(typeDescription) < 0) {
        this.currentLayout = typeDescription;
        this.widgetConfig.widgetSettings.currentLayout = this.currentLayout;
        break;
      }
    }

    setTimeout(() => {
      this.eventBus.broadcastEvent('widgetsChanged');
    })
  }

  /**
   * Layout changed event handler
  * @param newLayout {string} New layout
   */
  layoutChanged(_newLayout: string) {
    this.layoutHasChanged = true;

    const layout = this.currentLayout;
    this.widgetConfig.widgetSettings.currentLayout = layout;
    const typeId = this.reflists['ref_report_textwidget'].find(o => o.description === layout).id;
    const languageDefinitionId: number = this.appLocalization.getReportDesignerDictionaryConfig().id;

    if (this.templateId) {
      this.loadTemplateForType(typeId, languageDefinitionId).pipe(first()).subscribe(data => {
        this.widgetData = this.preprocessLoadedData(data);
      });
    } else if (!this.mockup) {
      this.loadForType(typeId, languageDefinitionId).pipe(first()).subscribe(data => {
        this.widgetData = this.preprocessLoadedData(data);
      });
    }
    else {
      this.widgetData = this.preprocessLoadedData({});
    }
  }

  /**
   * Edit mode changed
   * @param newEditMode
   */
  protected editModeChanged(newEditMode: boolean) {
    if (newEditMode) {
      this.widgetResized();
    }
  }

  /**
   * Available layouts
   */
  layouts = textWidgetLayouts;

  /**
   * Value changed
   * @param newValue
   */
  valueChange(newValue: string) {
    !environment.production && console.log('textwidget valueChange');
    this.textWidgetText = newValue;
    if (this.editMode) {
      this.widgetData.textWidgetText = newValue;

      this.previewContent = TextWidgetComponent.removeUnwantedTag(newValue);
    }
    this.forceDirty();
    this.raiseKendoEvent("onValueChanged");
  }

  /**
   * The widget was focused
   */
  public onFocus(): void {
    !environment.production && console.log('textwidget onFocus');
    this.raiseKendoEvent("focus");
  }

  /**
   * Blur event handler
   */
  public onBlur(): void {
    !environment.production && console.log('textwidget onBlur');
    this.raiseKendoEvent("blur");
  }

  /**
   * Paste event handler
   * @param e
   */
  public onPaste(e: EditorPasteEvent): void {
    !environment.production && console.log('textwidget onPaste', e.cleanedHtml);
  }

  /**
   * Widget resized event handler
   */
  protected widgetResized() {
    if (this.widgetConfig.widgetSettings.showKendoEditor) {
      //editor
      if (this.editorIframe.editor) {
        setTimeout(() => {
          this.iframeEditorResized();
        }, 50);//give some time for kendo to init iframe
      }

    } else if (this.editorInline.editor) {
      //inline editor
      setTimeout(() => {
        this.inlineEditorResized();
      });
    }
  }

  /**
   * closestParent DOM helper
   * @param elem {any} DOM element
   * @param className {any} CSS class name
   */
  private closestParent(elem: HTMLElement, className: string): HTMLElement {
    const parent = elem.parentNode as HTMLElement;
    const isMatch = (parent?.classList?.contains(className))
      || (parent?.className?.indexOf(className) !== -1);

    return isMatch ? parent : this.closestParent(parent, className);
  }

  /**
   * IFRAME editor resized event handler
   */
  private iframeEditorResized() {
    const rootEl: HTMLElement = this.componentEl.nativeElement;
    const iFrameBodyEl = rootEl.querySelector('iframe').contentDocument.querySelector('body');// IFRAME body
    if (iFrameBodyEl) {
      iFrameBodyEl.style.overflowY = "hidden";

      const widgetGridsterItemEl = this.closestParent(rootEl, "gridster-item");

      const widgetEl: HTMLElement = widgetGridsterItemEl.querySelector(`widget-box`);
      if (widgetEl) {
        const kEditorEl: HTMLElement = widgetEl.querySelector(".k-editor");
        if (kEditorEl) {
          //Bug 5808: Alignment Text widgets
          const widthAdjustment = 20;//27
          const heightAdjustment = 25;

          const newHeight = widgetGridsterItemEl.offsetHeight;
          const newWidth = widgetGridsterItemEl.offsetWidth;

          kEditorEl.style.height = (newHeight - heightAdjustment) + "px";
          kEditorEl.style.width = (newWidth - widthAdjustment) + "px";

          const contentEl: HTMLElement = kEditorEl.querySelector(".k-editor-content");
          if (contentEl) {
            contentEl.style.height = (newHeight - 25 - 50) + "px";
            contentEl.style.width = (newWidth - widthAdjustment) + "px";

            //force overflow button refresh/show
            if (this.editMode) {
              this.editorIframe.editor.toolbar.toggle(true);
            }
          }
        }
      }
    }
  }

  /**
   * Inline editor resized event handler
   */
  private inlineEditorResized() {
    const rootEl = this.componentEl.nativeElement as HTMLElement;
    const widgetGridsterItemEl = this.closestParent(rootEl, "gridster-item");

    const widgetEl = widgetGridsterItemEl.querySelector(`widget-box`);
    if (widgetEl) {
      const kEditorEl = widgetEl.querySelector<HTMLElement>(".k-editor");
      if (kEditorEl) {
        //Bug 5808: Alignment Text widgets
        const widthAdjustment = 22;//27
        const heightAdjustment = 0;

        const newHight = widgetGridsterItemEl.offsetHeight;
        const newWidth = widgetGridsterItemEl.offsetWidth;

        kEditorEl.style.height = (newHight - heightAdjustment) + "px";
        kEditorEl.style.width = (newWidth - widthAdjustment) + "px";
      }
    }
  }

  /**
   * Raise Kendo event to hashtag directive
   */
  private raiseKendoEvent(kendoEvent: 'focus' | 'blur' | 'onValueChanged') {
    this.hashtagDirective.handleKendoEvent(kendoEvent);
  }

  /**
   * Text pasted into widget event handler
   * @param event {any} Event object
   */
  pasteEventHandler(event) {
    //for Chrome, doesn't work in IE
    //used to get css (classes) styles for pasted data
    this.pastedData = event.clipboardData?.getData('text/html');
  }

  //#region HTML helpers

  /**
   * Internal HTML helper: removeUnwantedTag
   */
  static removeUnwantedTag(text) {
    if (text) {
      if (TextWidgetComponent.shouldRemoveUnwantedTags) {
        text = text.split("</p><p>").join("<br />");
        if (text.indexOf('<p>') === 0) {
          text = text.split("<p>").join("");
          text = text.split("</p>").join("");
        }
      }

      return text;
    }

    return "";
  }

  /**
   * Internal HTML helper: removeTextFormatting
   */
  static removeTextFormatting(element: HTMLElement) {
    if (element.style.fontFamily) {
      element.style.fontFamily = '';
    }
    if (element.style.fontSize) {
      element.style.fontSize = '14px';
    }

    for (const child of Array.from(element.children)) {
      if (child instanceof HTMLElement) {
        this.removeTextFormatting(child);
      }
    }

    this.setFont(element, 'span');
    this.setFont(element, 'p');
    this.setFont(element, 'em');
    this.setFont(element, 'i');
  }

  /**
   * Set the font
   * @param element
   * @param selector
   */
  static setFont(element: HTMLElement, selector: string): void {
    const elements = Array.from(element.querySelectorAll<HTMLElement>(selector));
    for (const el of elements) {
      if (el.style.fontFamily) {
        el.style.fontFamily = '';
      }
      if (el.style.fontSize) {
        el.style.fontSize = '14px';
      }
    }
  }

  /**
   * Internal HTML helper: applyRulesStyles
   */
  static applyRulesStyles(cssClasses: any, rule: any) {
    if (rule.selectorText.indexOf(".") === 0) {
      let _class = rule.selectorText;
      const stylesString = rule.cssText.trim().replace(_class, "").replace("{", "").replace("}", "").trim();
      const stylesSplitted = stylesString.split(";");
      const styles = [];
      stylesSplitted.forEach((obj) => {
        const parts = obj.split(":");
        if (parts.length === 2) {
          const style = parts[0].trim().toLowerCase();
          //we will apply just few styles to be as close compatible to IE behavior
          if (style.indexOf("background") !== -1//Don't use === 0
            || style.indexOf("color") !== -1
            || style.indexOf("font-weight") !== -1
            || style.indexOf("font-style") !== -1
            || style.indexOf("text-decoration") !== -1
          ) {
            styles.push(style + ":" + parts[1].trim());
          }
        }
      });

      if (styles.length) {
        _class = _class.substring(1).toLowerCase();
        cssClasses[_class] = styles.join(";");
      }
    }
  }

  /**
   * Internal HTML helper: applyChromePasteStyles
   */
  static applyChromePasteStyles(pasteContainerElement: HTMLElement, pastedData): void {
    try {
      const cssClasses = {};
      const xmlString = pastedData
        , parser = new DOMParser()
        , doc = parser.parseFromString(xmlString, "text/html");

      const styleElements = doc.getElementsByTagName("style");
      for (const styleElement of Array.from(styleElements)) {
        if (styleElement?.sheet?.cssRules) {
          const rules = styleElement.sheet.cssRules;
          for (const rule of Array.from(rules)) {
            TextWidgetComponent.applyRulesStyles(cssClasses, rule);
          }
        }
      }

      const el = pasteContainerElement;
      const nodes = Array.from(el.getElementsByTagName("*"));
      for (const node of nodes) {
        const className = node.classList.value.toLowerCase();
        if (cssClasses[className]) {
          console.log(node.classList.value);
          const style = node.getAttribute("style");
          const newStyle = style + ";" + cssClasses[className];
          node.setAttribute("style", newStyle);
        }
      }
    } catch (e) {
      //ignore errors (until we learn what all can be pasted) - who knows what users will paste
      // (but should work for "normal" stuff)
    }
  }

  //#endregion HTML helpers
}
